import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchInitiatives } from '../../utils/api';
import { InitiativesDB } from '../../utils/initiativesData';
import { Button } from '../ui';

const STATUS_OPTIONS = ['on-track', 'at-risk', 'off-track', 'on-hold', 'completed'];

export default function StatusUpdateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const currentUser = {
    id: user?.attributes?.sub || 'unknown',
    name: user?.attributes?.name || 'Unknown User',
  };
  const [initiative, setInitiative] = useState(null);
  const [form, setForm] = useState({
    updateDate: new Date().toISOString().split('T')[0],
    status: 'on-track',
    summary: '',
    details: '',
    percentComplete: 0,
    accomplishments: '',
    blockers: '',
    nextSteps: ''
  });

  useEffect(() => {
    if (!id) return;
    fetchInitiatives('all', getToken).then(list => {
      const data = (Array.isArray(list) ? list : []).find(i => i.id === id);
      setInitiative(data || null);
      if (data) {
        setForm(prev => ({
          ...prev,
          percentComplete: data.percentComplete ?? 0,
          status: data.status || 'on-track'
        }));
      }
    }).catch(() => setInitiative(null));
  }, [id, getToken]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const update = {
      id: InitiativesDB.generateId('UPDATE'),
      initiativeId: id,
      authorId: currentUser.id,
      authorName: currentUser.name,
      updateDate: form.updateDate,
      status: form.status,
      summary: form.summary,
      details: form.details || undefined,
      percentComplete: form.percentComplete,
      accomplishments: form.accomplishments ? form.accomplishments.split('\n').filter(Boolean) : [],
      blockers: form.blockers ? form.blockers.split('\n').filter(Boolean) : [],
      nextSteps: form.nextSteps ? form.nextSteps.split('\n').filter(Boolean) : []
    };
    InitiativesDB.saveStatusUpdate(update);
    navigate(`/strategic-initiatives/${id}`);
  };

  const handleCancel = () => navigate(`/strategic-initiatives/${id}`);

  if (!initiative) {
    return (
      <div className="dashboard-container">
        <p className="text-text-dark-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Button type="button" variant="default" size="sm" onClick={handleCancel} style={{ marginBottom: 12 }}>← Back to Initiative</Button>
        <h1 className="dashboard-title">Submit Status Update</h1>
        <p className="dashboard-subtitle">{initiative.name}</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ maxWidth: 640, padding: 24 }}>
        <div style={{ padding: 0 }}>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Update Date *</label>
            <input type="date" value={form.updateDate} onChange={e => handleChange('updateDate', e.target.value)} required className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="mb-4">
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Status</label>
              <select value={form.status} onChange={e => handleChange('status', e.target.value)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Progress %</label>
              <input type="number" min={0} max={100} value={form.percentComplete} onChange={e => handleChange('percentComplete', Number(e.target.value) || 0)} className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Summary *</label>
            <input type="text" value={form.summary} onChange={e => handleChange('summary', e.target.value)} required className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" placeholder="Brief summary of progress" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Details</label>
            <textarea value={form.details} onChange={e => handleChange('details', e.target.value)} rows={3} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ fontFamily: 'inherit', resize: 'vertical' }} placeholder="Additional details" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Accomplishments (one per line)</label>
            <textarea value={form.accomplishments} onChange={e => handleChange('accomplishments', e.target.value)} rows={3} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ fontFamily: 'inherit', resize: 'vertical' }} placeholder="Key accomplishments" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Blockers (one per line)</label>
            <textarea value={form.blockers} onChange={e => handleChange('blockers', e.target.value)} rows={2} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ fontFamily: 'inherit', resize: 'vertical' }} placeholder="Blockers or risks" />
          </div>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">Next Steps (one per line)</label>
            <textarea value={form.nextSteps} onChange={e => handleChange('nextSteps', e.target.value)} rows={3} className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30" style={{ fontFamily: 'inherit', resize: 'vertical' }} placeholder="Planned next steps" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button type="submit" variant="primary" size="sm">Save Update</Button>
            <Button type="button" variant="default" size="sm" onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
